// src/components/SupportAdminSettings.jsx
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import api from '../services/api';
import './SupportAdminSettings.css';
import {
  FaPlus, FaPencilAlt, FaTrash, FaSpinner, FaTimes, FaSave,
  FaFileExcel, FaUpload, FaDownload, FaCommentDots, FaListUl,
  FaSitemap, FaStopwatch, FaCogs, FaHistory, FaCheckDouble,
  FaSearch, FaInfoCircle, FaLayerGroup, FaChevronRight,
  FaShieldAlt, FaTag, FaGlobeAmericas, FaUsersCog, FaBullseye,
} from 'react-icons/fa';

// ─── Endpoints ───────────────────────────────────────────────────────────────
const CFG = '/api/admin/suporte-config';      // categorias/scripts/causas/sla por tenant
const CAT = '/api/admin/suporte-catalogo';    // catálogo global (admin_geral)

// ─── Helpers ──────────────────────────────────────────────────────────────────
const authCfg = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const PRIO_META = {
  Urgente: { color: '#dc2626', bg: '#fdeced', border: '#f6c9cd' },
  Alta:    { color: '#d97706', bg: '#fdf1e1', border: '#f6dcb4' },
  Normal:  { color: '#2563eb', bg: '#eaf1ff', border: '#cfe0ff' },
  Baixa:   { color: '#6b7890', bg: '#f6f8fb', border: '#e3e8f0' },
};

const TIPOS  = ['Incidente', 'Solicitação', 'Problema', 'Mudança', 'Outros'];
const PRIOS  = ['Urgente', 'Alta', 'Normal', 'Baixa'];
const PERFIS = [
  { value: 'suporte_tecnico', label: 'Suporte Técnico' },
  { value: 'financeiro',      label: 'Financeiro' },
  { value: 'operacional',     label: 'Operacional' },
  { value: 'rh',              label: 'RH' },
  { value: 'admin',           label: 'Administração' },
];

// ════════════════════════════════════════════════════════════════════════════
// SLA
// ════════════════════════════════════════════════════════════════════════════
const SlaPoliciesSettings = () => {
  const PRIORIDADES = ['Urgente', 'Alta', 'Normal', 'Baixa'];
  const [politicas, setPoliticas] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);

  const fetchPoliticas = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`${CFG}/sla-politicas`, authCfg());
      setPoliticas(PRIORIDADES.map(p =>
        data.find(d => d.prioridade === p) ||
        { prioridade: p, tempo_primeira_resposta_minutos: 60, tempo_resolucao_minutos: 480 }
      ));
    } catch { toast.error('Erro ao carregar SLA.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPoliticas(); }, [fetchPoliticas]);

  const handleChange = (prio, field, val) =>
    setPoliticas(prev => prev.map(p => p.prioridade === prio ? { ...p, [field]: parseInt(val) || 0 } : p));

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`${CFG}/sla-politicas`, politicas, authCfg());
      toast.success('SLA salvo com sucesso!');
    } catch { toast.error('Erro ao salvar SLA.'); }
    finally { setSaving(false); }
  };

  if (loading) return <ModuleLoader label="Carregando SLA…" />;

  return (
    <ModuleCard
      icon={<FaStopwatch />} iconClass="sla"
      title="Service Level Agreements"
      subtitle="Tempos de resposta e resolução por prioridade"
      action={
        <button className="btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? <FaSpinner className="spin" /> : <FaSave />} Salvar
        </button>
      }
    >
      <div className="sla-grid">
        {politicas.map(p => {
          const meta = PRIO_META[p.prioridade] || PRIO_META.Normal;
          return (
            <div className="sla-row" key={p.prioridade} style={{ '--pc': meta.color, '--pb': meta.bg, '--pbr': meta.border }}>
              <div className="sla-prio"><span className="sla-dot" /><strong>{p.prioridade}</strong></div>
              <div className="sla-field-group">
                <label>1ª Resposta</label>
                <div className="sla-input-wrap">
                  <input type="number" min="1" value={p.tempo_primeira_resposta_minutos}
                    onChange={e => handleChange(p.prioridade, 'tempo_primeira_resposta_minutos', e.target.value)} />
                  <span className="sla-unit">min</span>
                </div>
                <span className="sla-hint">{(p.tempo_primeira_resposta_minutos / 60).toFixed(1)}h</span>
              </div>
              <div className="sla-field-group">
                <label>Resolução</label>
                <div className="sla-input-wrap">
                  <input type="number" min="1" value={p.tempo_resolucao_minutos}
                    onChange={e => handleChange(p.prioridade, 'tempo_resolucao_minutos', e.target.value)} />
                  <span className="sla-unit">min</span>
                </div>
                <span className="sla-hint">{(p.tempo_resolucao_minutos / 60).toFixed(1)}h</span>
              </div>
              <span className="sla-badge-active">Ativo</span>
            </div>
          );
        })}
      </div>
    </ModuleCard>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// SCRIPTS
// ════════════════════════════════════════════════════════════════════════════
const ScriptsSettings = () => {
  const [scripts,   setScripts]   = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [current,   setCurrent]   = useState({ cmd: '', label: '', text: '' });
  const [loading,   setLoading]   = useState(false);
  const [search,    setSearch]    = useState('');

  const fetch_ = async () => {
    setLoading(true);
    try { const { data } = await api.get(`${CFG}/scripts`, authCfg()); setScripts(data); }
    catch { toast.error('Erro ao carregar scripts.'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetch_(); }, []);

  const handleSave = async () => {
    if (!current.cmd || !current.text) return toast.warn('Preencha comando e texto.');
    const cmd = current.cmd.startsWith('/') ? current.cmd : `/${current.cmd}`;
    try {
      if (current.id) await api.put(`${CFG}/scripts/${current.id}`, { ...current, cmd }, authCfg());
      else            await api.post(`${CFG}/scripts`, { ...current, cmd }, authCfg());
      toast.success('Script salvo!'); setModalOpen(false); fetch_();
    } catch { toast.error('Erro ao salvar script.'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir este script?')) return;
    try { await api.delete(`${CFG}/scripts/${id}`, authCfg()); toast.success('Script removido.'); fetch_(); }
    catch { toast.error('Falha ao excluir.'); }
  };

  const filtered = scripts.filter(s =>
    s.label?.toLowerCase().includes(search.toLowerCase()) ||
    s.cmd?.toLowerCase().includes(search.toLowerCase()) ||
    s.text?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <ModuleLoader label="Carregando scripts…" />;

  return (
    <ModuleCard
      icon={<FaCommentDots />} iconClass="script"
      title="Fraseologia & Scripts Rápidos"
      subtitle="Gatilhos de texto para agilizar a comunicação dos agentes"
      action={
        <button className="btn-primary" onClick={() => { setCurrent({ cmd: '', label: '', text: '' }); setModalOpen(true); }}>
          <FaPlus /> Novo Script
        </button>
      }
    >
      <div className="scripts-toolbar">
        <div className="search-box">
          <FaSearch />
          <input type="text" placeholder="Buscar por comando ou rótulo…" value={search} onChange={e => setSearch(e.target.value)} />
          {search && <button className="search-clear" onClick={() => setSearch('')}><FaTimes /></button>}
        </div>
        <span className="scripts-count">{filtered.length} script{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      <div className="info-banner">
        <FaInfoCircle />
        <span>Use <code>{'{nome}'}</code> para interpolação dinâmica. O comando <code>/ts</code> é reservado pelo sistema.</span>
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={<FaCommentDots />} label={search ? `Nenhum script para "${search}"` : 'Nenhum script cadastrado'} />
      ) : (
        <div className="scripts-grid">
          {filtered.map(s => (
            <div className={`script-card ${s.cmd === '/ts' ? 'reserved' : ''}`} key={s.id}>
              <div className="sc-head">
                <code className="sc-cmd">{s.cmd}</code>
                <div className="sc-actions">
                  <button className="ic-btn edit" title="Editar" onClick={() => { setCurrent(s); setModalOpen(true); }}><FaPencilAlt /></button>
                  <button className="ic-btn del" title="Excluir" onClick={() => handleDelete(s.id)}><FaTrash /></button>
                </div>
              </div>
              <div className="sc-body"><h5>{s.label}</h5><p>{s.text}</p></div>
            </div>
          ))}
        </div>
      )}

      {modalOpen && (
        <Dialog title={current.id ? 'Editar Script' : 'Novo Script'} onClose={() => setModalOpen(false)} onConfirm={handleSave} confirmLabel="Salvar Script">
          <Field label="Gatilho (ex: /saudacao)">
            <input type="text" value={current.cmd} onChange={e => setCurrent(p => ({ ...p, cmd: e.target.value }))} placeholder="/ola" />
          </Field>
          <Field label="Rótulo descritivo">
            <input type="text" value={current.label} onChange={e => setCurrent(p => ({ ...p, label: e.target.value }))} placeholder="Saudação inicial" />
          </Field>
          <Field label="Mensagem">
            <textarea rows={5} value={current.text} onChange={e => setCurrent(p => ({ ...p, text: e.target.value }))} placeholder="Olá {nome}, como posso ajudar?" />
          </Field>
        </Dialog>
      )}
    </ModuleCard>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// CAUSAS
// ════════════════════════════════════════════════════════════════════════════
const CausesSettings = () => {
  const [causes,   setCauses]   = useState([]);
  const [newCause, setNewCause] = useState('');
  const [loading,  setLoading]  = useState(false);
  const [adding,   setAdding]   = useState(false);
  const inputRef = useRef(null);

  const fetchCauses = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`${CFG}/causas`, authCfg()); setCauses(data); }
    catch { toast.error('Erro ao carregar causas.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchCauses(); }, [fetchCauses]);

  const handleAdd = async () => {
    if (!newCause.trim() || adding) return;
    setAdding(true);
    try {
      await api.post(`${CFG}/causas`, { descricao: newCause.trim() }, authCfg());
      setNewCause(''); fetchCauses(); toast.success('Causa cadastrada!'); inputRef.current?.focus();
    } catch { toast.error('Falha ao adicionar.'); }
    finally { setAdding(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remover esta causa?')) return;
    try { await api.delete(`${CFG}/causas/${id}`, authCfg()); fetchCauses(); toast.success('Causa removida.'); }
    catch { toast.error('Erro ao remover.'); }
  };

  if (loading) return <ModuleLoader label="Carregando causas…" />;

  return (
    <ModuleCard icon={<FaListUl />} iconClass="cause" title="Causas Raiz de Encerramento"
      subtitle="Obriga o agente a categorizar o motivo ao finalizar um ticket">
      <div className="cause-input-row">
        <input ref={inputRef} type="text" placeholder="Ex: Falha de conectividade, Erro de configuração…"
          value={newCause} onChange={e => setNewCause(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleAdd()} disabled={adding} />
        <button className="btn-primary" onClick={handleAdd} disabled={adding || !newCause.trim()}>
          {adding ? <FaSpinner className="spin" /> : <FaPlus />} Adicionar
        </button>
      </div>
      {causes.length === 0 ? (
        <EmptyState icon={<FaShieldAlt />} label="Nenhuma causa cadastrada ainda" />
      ) : (
        <ul className="cause-list">
          {causes.map((c, i) => (
            <li key={c.id} style={{ animationDelay: `${i * 30}ms` }}>
              <span className="cause-bullet" />
              <span className="cause-text">{c.descricao}</span>
              <button className="ic-btn del" onClick={() => handleDelete(c.id)} title="Remover"><FaTrash /></button>
            </li>
          ))}
        </ul>
      )}
    </ModuleCard>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// CATEGORIAS DO TENANT (local)
// ════════════════════════════════════════════════════════════════════════════
const CategoriesSettings = ({ isAdminGeral }) => {
  const [categorias, setCategorias] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [modalInfo,  setModalInfo]  = useState({ isOpen: false, item: null, type: '' });
  const fileRef = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`${CFG}/categorias`, authCfg()); setCategorias(data); }
    catch { toast.error('Erro ao carregar categorias.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (item, type) => {
    if (!window.confirm(`Excluir "${item.nome}"?`)) return;
    const ep = type === 'Categoria' ? `${CFG}/categorias/${item.id}` : `${CFG}/subcategorias/${item.id}`;
    try { await api.delete(ep, authCfg()); toast.success('Excluído com sucesso!'); fetchData(); }
    catch { toast.error('Falha ao excluir.'); }
  };

  const handleSave = async (payload) => {
    const { type } = modalInfo;
    const isEdit = !!payload.id;
    const ep = type === 'Categoria'
      ? (isEdit ? `${CFG}/categorias/${payload.id}` : `${CFG}/categorias`)
      : (isEdit ? `${CFG}/subcategorias/${payload.id}` : `${CFG}/subcategorias`);
    try {
      await api[isEdit ? 'put' : 'post'](ep, payload, authCfg());
      toast.success(`${type} salva!`); setModalInfo({ isOpen: false, item: null, type: '' }); fetchData();
    } catch { toast.error('Erro ao salvar.'); }
  };

  // Import/Export (apenas admin_geral) — opera sobre as categorias do tenant ativo
  const handleBulk = (action) => {
    if (action === 'modelo') {
      const tpl = [
        { 'Categoria Principal': 'Financeiro', Subcategoria: 'Contas a pagar', Departamento: 'financeiro', Tipo: 'Incidente', Prioridade: 'Normal' },
        { 'Categoria Principal': 'Hardware', Subcategoria: 'Monitor com defeito', Departamento: 'suporte_tecnico', Tipo: 'Incidente', Prioridade: 'Alta' },
      ];
      const ws = XLSX.utils.json_to_sheet(tpl); const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Modelo'); XLSX.writeFile(wb, 'modelo_importacao.xlsx');
      toast.info('Modelo gerado!');
    } else if (action === 'exportar') {
      const rows = categorias.flatMap(cat =>
        (cat.subcategorias?.length ? cat.subcategorias : [{ nome: '(Nenhuma)' }]).map(sub => ({
          Categoria: cat.nome, Subcategoria: sub.nome,
          Departamento: cat.perfil_destino_padrao?.toUpperCase(), Tipo: cat.tipo_padrao, Prioridade: cat.prioridade_padrao,
        }))
      );
      const ws = XLSX.utils.json_to_sheet(rows); ws['!cols'] = [25, 25, 20, 15, 15].map(wch => ({ wch }));
      const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, 'Categorias'); XLSX.writeFile(wb, 'categorias_suporte.xlsx');
      toast.success('Exportação concluída!');
    } else if (action === 'importar') { fileRef.current.click(); }
  };

  const handleImportFile = (e) => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: 'binary' });
        const data = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]);
        if (!data.length) return toast.warn('O arquivo está vazio.');
        await api.post(`${CFG}/categorias/importar`, data, authCfg());
        toast.success(`${data.length} itens importados!`); fetchData();
      } catch { toast.error('Erro ao processar arquivo.'); }
      finally { e.target.value = null; }
    };
    reader.readAsBinaryString(file);
  };

  if (loading) return <FullPageLoader />;

  return (
    <div className="tab-pane">
      <div className="section-header">
        <div>
          <h2>Hierarquia de Atendimento</h2>
          <p>{categorias.length} categoria{categorias.length !== 1 ? 's' : ''} · {categorias.reduce((a, c) => a + (c.subcategorias?.length || 0), 0)} subcategorias deste tenant</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {isAdminGeral && (
            <div className="bulk-actions">
              <button className="btn-outline" onClick={() => handleBulk('modelo')}><FaFileExcel /> Modelo</button>
              <button className="btn-outline" onClick={() => handleBulk('importar')}><FaUpload /> Importar</button>
              <button className="btn-outline" onClick={() => handleBulk('exportar')}><FaDownload /> Exportar</button>
              <input type="file" ref={fileRef} hidden accept=".xlsx,.xls" onChange={handleImportFile} />
            </div>
          )}
          <button className="btn-primary" onClick={() => setModalInfo({ isOpen: true, item: null, type: 'Categoria' })}>
            <FaPlus /> Nova Categoria
          </button>
        </div>
      </div>

      {categorias.length === 0 ? (
        <EmptyState icon={<FaSitemap />} label="Nenhuma categoria cadastrada neste tenant"
          cta={{ label: 'Criar primeira categoria', onClick: () => setModalInfo({ isOpen: true, item: null, type: 'Categoria' }) }} />
      ) : (
        <div className="cat-grid">
          {categorias.map((cat, i) => (
            <div className="cat-card" key={cat.id} style={{ animationDelay: `${i * 40}ms` }}>
              <div className="cat-card-head">
                <div className="cat-card-info">
                  <h4>{cat.nome}</h4>
                  <div className="cat-tags">
                    <span className={`dept-pill dept-${cat.perfil_destino_padrao}`}>{cat.perfil_destino_padrao?.toUpperCase() || '—'}</span>
                    <span className="type-pill">{cat.tipo_padrao}</span>
                    {cat.prioridade_padrao && <span className={`prio-pill prio-${cat.prioridade_padrao?.toLowerCase()}`}>{cat.prioridade_padrao}</span>}
                  </div>
                </div>
                <div className="cat-card-ops">
                  <button className="ic-btn edit" title="Editar" onClick={() => setModalInfo({ isOpen: true, item: cat, type: 'Categoria' })}><FaPencilAlt /></button>
                  <button className="ic-btn add-sub" title="Adicionar subcategoria" onClick={() => setModalInfo({ isOpen: true, item: { categoria_id: cat.id }, type: 'Subcategoria' })}><FaLayerGroup /></button>
                  <button className="ic-btn del" title="Excluir" onClick={() => handleDelete(cat, 'Categoria')}><FaTrash /></button>
                </div>
              </div>
              <div className="cat-card-body">
                {cat.subcategorias?.length ? (
                  <ul className="sub-list">
                    {cat.subcategorias.map(sub => (
                      <li key={sub.id}>
                        <FaTag className="sub-icon" /><span>{sub.nome}</span>
                        <div className="sub-ops">
                          <FaPencilAlt title="Editar" onClick={() => setModalInfo({ isOpen: true, item: sub, type: 'Subcategoria' })} />
                          <FaTrash title="Excluir" onClick={() => handleDelete(sub, 'Subcategoria')} />
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <button className="sub-empty-cta" onClick={() => setModalInfo({ isOpen: true, item: { categoria_id: cat.id }, type: 'Subcategoria' })}>
                    <FaPlus /> Adicionar subcategoria
                  </button>
                )}
              </div>
              <div className="cat-card-foot">
                <span>{cat.subcategorias?.length || 0} subcategoria{cat.subcategorias?.length !== 1 ? 's' : ''}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {modalInfo.isOpen && (
        <CategoryModal item={modalInfo.item} type={modalInfo.type} categories={categorias}
          onClose={() => setModalInfo({ isOpen: false, item: null, type: '' })} onSave={handleSave} />
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// CATÁLOGO GLOBAL (admin_geral)  — categorias replicadas para múltiplos tenants
// ════════════════════════════════════════════════════════════════════════════
const GlobalCatalogSettings = () => {
  const [categorias, setCategorias] = useState([]);
  const [tenants,    setTenants]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [tenLoading, setTenLoading] = useState(true);
  const [catModal,   setCatModal]   = useState({ open: false, item: null });
  const [subModal,   setSubModal]   = useState({ open: false, item: null, categoriaId: null });

  const tenantNameById = useMemo(() => {
    const m = new Map(); tenants.forEach(t => m.set(t.id, t.nome_empresa)); return m;
  }, [tenants]);

  const fetchCategorias = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`${CAT}/categorias`, authCfg()); setCategorias(data); }
    catch { toast.error('Erro ao carregar catálogo global.'); }
    finally { setLoading(false); }
  }, []);

  const fetchTenants = useCallback(async () => {
    setTenLoading(true);
    try {
      // Fonte única de verdade dos tenants: SystemHub (clientes_sistema)
      const { data } = await api.get('/api/system-hub/clientes', authCfg());
      setTenants(data);
    }
    catch { toast.error('Erro ao carregar a lista de tenants.'); }
    finally { setTenLoading(false); }
  }, []);

  useEffect(() => { fetchCategorias(); fetchTenants(); }, [fetchCategorias, fetchTenants]);

  const saveCategoria = async (payload) => {
    const isEdit = !!payload.id;
    try {
      if (isEdit) await api.put(`${CAT}/categorias/${payload.id}`, payload, authCfg());
      else        await api.post(`${CAT}/categorias`, payload, authCfg());
      toast.success('Categoria global salva!'); setCatModal({ open: false, item: null }); fetchCategorias();
    } catch (err) { toast.error(err.response?.data?.error || 'Erro ao salvar.'); }
  };

  const deleteCategoria = async (cat) => {
    if (!window.confirm(`Excluir a categoria global "${cat.nome}"?`)) return;
    try { await api.delete(`${CAT}/categorias/${cat.id}`, authCfg()); toast.success('Removida.'); fetchCategorias(); }
    catch (err) { toast.error(err.response?.data?.error || 'Falha ao excluir.'); }
  };

  const saveSub = async (payload) => {
    try {
      if (payload.id) await api.put(`${CAT}/subcategorias/${payload.id}`, { nome: payload.nome }, authCfg());
      else            await api.post(`${CAT}/subcategorias`, { categoria_id: payload.categoria_id, nome: payload.nome }, authCfg());
      toast.success('Subcategoria salva!'); setSubModal({ open: false, item: null, categoriaId: null }); fetchCategorias();
    } catch { toast.error('Erro ao salvar subcategoria.'); }
  };

  const deleteSub = async (sub) => {
    if (!window.confirm(`Excluir "${sub.nome}"?`)) return;
    try { await api.delete(`${CAT}/subcategorias/${sub.id}`, authCfg()); toast.success('Removida.'); fetchCategorias(); }
    catch { toast.error('Falha ao excluir.'); }
  };

  if (loading) return <FullPageLoader />;

  const scopeLabel = (cat) => {
    if (!cat.ativo) return { cls: 'inativo', icon: null, text: 'Inativa' };
    if (cat.escopo === 'GLOBAL_TODOS') return { cls: 'todos', icon: <FaGlobeAmericas />, text: 'Todos os tenants' };
    return { cls: 'segmentado', icon: <FaBullseye />, text: `${cat.tenantIds?.length || 0} tenant(s)` };
  };

  return (
    <div className="tab-pane">
      <div className="section-header">
        <div>
          <h2>Catálogo Global de Categorias</h2>
          <p>Cadastre uma vez e distribua para todos os tenants ou apenas os selecionados — aparece no formulário de abertura de chamado de cada tenant.</p>
        </div>
        <button className="btn-primary" onClick={() => setCatModal({ open: true, item: null })}>
          <FaPlus /> Nova Categoria Global
        </button>
      </div>

      <div className="info-banner">
        <FaInfoCircle />
        <span><strong>Todos os tenants</strong> é dinâmico (alcança tenants criados no futuro). <strong>Segmentado</strong> fixa os tenants escolhidos.</span>
      </div>

      {categorias.length === 0 ? (
        <EmptyState icon={<FaGlobeAmericas />} label="Nenhuma categoria global cadastrada"
          cta={{ label: 'Criar primeira categoria global', onClick: () => setCatModal({ open: true, item: null }) }} />
      ) : (
        <div className="cat-grid">
          {categorias.map((cat, i) => {
            const sc = scopeLabel(cat);
            return (
              <div className="cat-card" key={cat.id} style={{ animationDelay: `${i * 40}ms` }}>
                <div className="cat-card-head">
                  <div className="cat-card-info">
                    <h4>{cat.nome}</h4>
                    <div className="cat-tags">
                      <span className={`scope-pill ${sc.cls}`}>{sc.icon}{sc.text}</span>
                      <span className={`dept-pill dept-${cat.perfil_destino_padrao}`}>{cat.perfil_destino_padrao?.toUpperCase() || '—'}</span>
                      <span className="type-pill">{cat.tipo_padrao}</span>
                      {cat.prioridade_padrao && <span className={`prio-pill prio-${cat.prioridade_padrao?.toLowerCase()}`}>{cat.prioridade_padrao}</span>}
                    </div>
                  </div>
                  <div className="cat-card-ops">
                    <button className="ic-btn edit" title="Editar" onClick={() => setCatModal({ open: true, item: cat })}><FaPencilAlt /></button>
                    <button className="ic-btn add-sub" title="Adicionar subcategoria" onClick={() => setSubModal({ open: true, item: null, categoriaId: cat.id })}><FaLayerGroup /></button>
                    <button className="ic-btn del" title="Excluir" onClick={() => deleteCategoria(cat)}><FaTrash /></button>
                  </div>
                </div>
                <div className="cat-card-body">
                  {cat.subcategorias?.length ? (
                    <ul className="sub-list">
                      {cat.subcategorias.map(sub => (
                        <li key={sub.id}>
                          <FaTag className="sub-icon" /><span>{sub.nome}</span>
                          <div className="sub-ops">
                            <FaPencilAlt title="Editar" onClick={() => setSubModal({ open: true, item: sub, categoriaId: cat.id })} />
                            <FaTrash title="Excluir" onClick={() => deleteSub(sub)} />
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <button className="sub-empty-cta" onClick={() => setSubModal({ open: true, item: null, categoriaId: cat.id })}>
                      <FaPlus /> Adicionar subcategoria
                    </button>
                  )}
                </div>
                <div className="cat-card-foot">
                  <span>{cat.subcategorias?.length || 0} subcategoria{cat.subcategorias?.length !== 1 ? 's' : ''}</span>
                  {cat.escopo === 'SEGMENTADO' && cat.tenantIds?.length > 0 && (
                    <span title={cat.tenantIds.map(id => tenantNameById.get(id) || `#${id}`).join(', ')}>
                      {cat.tenantIds.slice(0, 2).map(id => tenantNameById.get(id) || `#${id}`).join(', ')}
                      {cat.tenantIds.length > 2 ? ` +${cat.tenantIds.length - 2}` : ''}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {catModal.open && (
        <GlobalCategoryModal item={catModal.item} tenants={tenants} tenantsLoading={tenLoading}
          onClose={() => setCatModal({ open: false, item: null })} onSave={saveCategoria} />
      )}
      {subModal.open && (
        <SimpleNameModal title={subModal.item ? 'Editar Subcategoria' : 'Nova Subcategoria'} initial={subModal.item?.nome || ''}
          placeholder="Ex: Problema de Rede"
          onClose={() => setSubModal({ open: false, item: null, categoriaId: null })}
          onConfirm={(nome) => saveSub({ id: subModal.item?.id, categoria_id: subModal.categoriaId, nome })} />
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL (orquestrador)
// ════════════════════════════════════════════════════════════════════════════
export default function SupportAdminSettings() {
  const [activeTab,    setActiveTab]    = useState('categorias');
  const [isAdminGeral, setIsAdminGeral] = useState(false);

  useEffect(() => {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    setIsAdminGeral(u?.perfil === 'admin_geral');
  }, []);

  const NAV = useMemo(() => ([
    { key: 'categorias', icon: <FaSitemap />,       label: 'Estrutura de Serviços' },
    ...(isAdminGeral ? [{ key: 'global', icon: <FaGlobeAmericas />, label: 'Catálogo Global', badge: 'GERAL' }] : []),
    { key: 'scripts',    icon: <FaCommentDots />,    label: 'Fraseologia & Scripts' },
    { key: 'governanca', icon: <FaCheckDouble />,    label: 'Governança & SLA' },
  ]), [isAdminGeral]);

  const activeLabel = NAV.find(n => n.key === activeTab)?.label || 'Configurações';

  return (
    <div className="sas-root">
      {/* ══ SIDEBAR ══ */}
      <aside className="sas-sidebar">
        <div className="sidebar-brand">
          <div className="sidebar-brand-icon"><FaCogs /></div>
          <div>
            <span className="sidebar-brand-name">Admin Center</span>
            <span className="sidebar-brand-sub">ITSM Compliance</span>
          </div>
        </div>
        <nav className="sidebar-nav">
          <p className="sidebar-section-label">Configurações</p>
          {NAV.map(n => (
            <button key={n.key} className={`sidebar-nav-btn ${activeTab === n.key ? 'active' : ''}`} onClick={() => setActiveTab(n.key)}>
              <span className="snb-icon">{n.icon}</span>
              <span className="snb-label">{n.label}</span>
              {n.badge && <span className="snb-badge">{n.badge}</span>}
              {activeTab === n.key && <FaChevronRight className="snb-arrow" />}
            </button>
          ))}
        </nav>
        <div className="sidebar-footer">
          <span className="sf-version">v3.0.0</span>
          <span className="sf-badge">Compliance</span>
        </div>
      </aside>

      {/* ══ MAIN ══ */}
      <main className="sas-main">
        <header className="sas-topbar">
          <div className="topbar-left">
            <h1>{activeLabel}</h1>
            <div className="topbar-breadcrumb">
              <span>Configurações</span><FaChevronRight /><span>Suporte</span><FaChevronRight />
              <span className="tb-current">{activeLabel}</span>
            </div>
          </div>
          {isAdminGeral && activeTab === 'global' && (
            <div className="topbar-right">
              <span className="sf-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <FaUsersCog /> Distribuição multitenant
              </span>
            </div>
          )}
        </header>

        <div className="sas-content">
          {activeTab === 'categorias' && <CategoriesSettings isAdminGeral={isAdminGeral} />}
          {activeTab === 'global' && isAdminGeral && <GlobalCatalogSettings />}
          {activeTab === 'scripts' && <div className="tab-pane"><ScriptsSettings /></div>}
          {activeTab === 'governanca' && (
            <div className="tab-pane gov-grid"><SlaPoliciesSettings /><CausesSettings /></div>
          )}
        </div>
      </main>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// MODAIS
// ════════════════════════════════════════════════════════════════════════════

// Categoria/Subcategoria do tenant (local)
const CategoryModal = ({ item, type, categories, onClose, onSave }) => {
  const [name,     setName]     = useState(item?.nome || '');
  const [perfil,   setPerfil]   = useState(item?.perfil_destino_padrao || 'suporte_tecnico');
  const [parentId, setParentId] = useState(item?.categoria_id || categories[0]?.id || '');
  const [tipo,     setTipo]     = useState(item?.tipo_padrao || 'Incidente');
  const [prio,     setPrio]     = useState(item?.prioridade_padrao || 'Normal');

  return (
    <Dialog title={`${item?.id ? 'Editar' : 'Cadastrar'} ${type}`} onClose={onClose}
      onConfirm={() => onSave({ id: item?.id, nome: name, perfil_destino_padrao: perfil, categoria_id: parentId, tipo_padrao: tipo, prioridade_padrao: prio })}
      confirmLabel="Gravar" wide>
      {type === 'Subcategoria' && (
        <Field label="Categoria pai">
          <select value={parentId} onChange={e => setParentId(e.target.value)}>
            {categories.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </select>
        </Field>
      )}
      <Field label="Nome">
        <input type="text" value={name} onChange={e => setName(e.target.value)} autoFocus
          placeholder={type === 'Categoria' ? 'Ex: Suporte Técnico' : 'Ex: Problema de Rede'} />
      </Field>
      {type === 'Categoria' && (
        <div className="form-row">
          <Field label="Departamento / Fila">
            <select value={perfil} onChange={e => setPerfil(e.target.value)}>
              {PERFIS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </Field>
          <Field label="Tipo de Ticket">
            <select value={tipo} onChange={e => setTipo(e.target.value)}>{TIPOS.map(t => <option key={t}>{t}</option>)}</select>
          </Field>
          <Field label="Prioridade Padrão">
            <select value={prio} onChange={e => setPrio(e.target.value)}>{PRIOS.map(p => <option key={p}>{p}</option>)}</select>
          </Field>
        </div>
      )}
    </Dialog>
  );
};

// Categoria do catálogo global (com escopo + seleção de tenants)
const GlobalCategoryModal = ({ item, tenants, tenantsLoading, onClose, onSave }) => {
  const [name,   setName]   = useState(item?.nome || '');
  const [perfil, setPerfil] = useState(item?.perfil_destino_padrao || 'suporte_tecnico');
  const [tipo,   setTipo]   = useState(item?.tipo_padrao || 'Incidente');
  const [prio,   setPrio]   = useState(item?.prioridade_padrao || 'Normal');
  const [escopo, setEscopo] = useState(item?.escopo || 'GLOBAL_TODOS');
  const [tenantIds, setTenantIds] = useState(item?.tenantIds || []);
  const [ativo, setAtivo] = useState(item?.ativo === undefined ? true : !!item.ativo);

  const submit = () => {
    if (!name.trim()) return toast.warn('Informe o nome da categoria.');
    if (escopo === 'SEGMENTADO' && tenantIds.length === 0) return toast.warn('Selecione ao menos um tenant.');
    onSave({
      id: item?.id, nome: name.trim(), perfil_destino_padrao: perfil,
      tipo_padrao: tipo, prioridade_padrao: prio, escopo,
      tenantIds: escopo === 'SEGMENTADO' ? tenantIds : [], ativo,
    });
  };

  return (
    <Dialog title={`${item?.id ? 'Editar' : 'Nova'} Categoria Global`} onClose={onClose} onConfirm={submit} confirmLabel="Gravar" wide>
      <Field label="Nome da categoria">
        <input type="text" value={name} onChange={e => setName(e.target.value)} autoFocus placeholder="Ex: Acesso e Senhas" />
      </Field>

      <div className="form-row">
        <Field label="Departamento / Fila">
          <select value={perfil} onChange={e => setPerfil(e.target.value)}>
            {PERFIS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </Field>
        <Field label="Tipo de Ticket">
          <select value={tipo} onChange={e => setTipo(e.target.value)}>{TIPOS.map(t => <option key={t}>{t}</option>)}</select>
        </Field>
        <Field label="Prioridade Padrão">
          <select value={prio} onChange={e => setPrio(e.target.value)}>{PRIOS.map(p => <option key={p}>{p}</option>)}</select>
        </Field>
      </div>

      <Field label="Alcance da categoria">
        <div className="segment">
          <button type="button" className={escopo === 'GLOBAL_TODOS' ? 'active' : ''} onClick={() => setEscopo('GLOBAL_TODOS')}>
            <FaGlobeAmericas /> Todos os tenants
          </button>
          <button type="button" className={escopo === 'SEGMENTADO' ? 'active' : ''} onClick={() => setEscopo('SEGMENTADO')}>
            <FaBullseye /> Segmentar
          </button>
        </div>
      </Field>

      {escopo === 'SEGMENTADO' && (
        <Field label={`Tenants selecionados (${tenantIds.length})`}>
          <TenantMultiSelect tenants={tenants} loading={tenantsLoading} selected={tenantIds} onChange={setTenantIds} />
        </Field>
      )}

      {item?.id && (
        <Field label="Status">
          <div className="segment">
            <button type="button" className={ativo ? 'active' : ''} onClick={() => setAtivo(true)}>Ativa</button>
            <button type="button" className={!ativo ? 'active' : ''} onClick={() => setAtivo(false)}>Inativa</button>
          </div>
        </Field>
      )}
    </Dialog>
  );
};

// Modal simples de 1 campo (subcategorias globais)
const SimpleNameModal = ({ title, initial = '', placeholder, onClose, onConfirm }) => {
  const [val, setVal] = useState(initial);
  return (
    <Dialog title={title} onClose={onClose} onConfirm={() => val.trim() ? onConfirm(val.trim()) : toast.warn('Informe um nome.')} confirmLabel="Gravar">
      <Field label="Nome">
        <input type="text" value={val} autoFocus placeholder={placeholder}
          onChange={e => setVal(e.target.value)} onKeyDown={e => e.key === 'Enter' && val.trim() && onConfirm(val.trim())} />
      </Field>
    </Dialog>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// PRIMITIVOS UI
// ════════════════════════════════════════════════════════════════════════════
const TenantMultiSelect = ({ tenants, selected, onChange, loading }) => {
  const [q, setQ] = useState('');
  const sel = new Set(selected);
  const filtered = tenants.filter(t => (t.nome_empresa || '').toLowerCase().includes(q.toLowerCase()));

  const toggle = (id) => {
    const next = new Set(sel);
    next.has(id) ? next.delete(id) : next.add(id);
    onChange([...next]);
  };
  const selectAll = () => onChange([...new Set([...sel, ...filtered.map(t => t.id)])]);
  const clearAll  = () => onChange(selected.filter(id => !filtered.some(t => t.id === id)));

  return (
    <div className="tenant-picker">
      <div className="tenant-picker-head">
        <FaSearch style={{ color: 'var(--dim)', fontSize: '.8rem' }} />
        <input type="text" placeholder="Buscar tenant…" value={q} onChange={e => setQ(e.target.value)} />
        <span className="tenant-picker-meta">{selected.length} selecionado(s)</span>
      </div>
      <div className="tenant-bulk">
        <button type="button" onClick={selectAll}>Selecionar visíveis</button>
        <span className="sep">·</span>
        <button type="button" onClick={clearAll}>Limpar visíveis</button>
      </div>
      <div className="tenant-list">
        {loading ? (
          <div className="tenant-empty"><FaSpinner className="spin" /> Carregando tenants…</div>
        ) : filtered.length === 0 ? (
          <div className="tenant-empty">Nenhum tenant encontrado.</div>
        ) : filtered.map(t => (
          <label className="tenant-item" key={t.id}>
            <input type="checkbox" checked={sel.has(t.id)} onChange={() => toggle(t.id)} />
            <span className="ti-name">{t.nome_empresa}</span>
            <span className={`ti-status ${(t.status || 'ativo').toLowerCase()}`}>{t.status || 'ativo'}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

const ModuleCard = ({ icon, iconClass, title, subtitle, action, children }) => (
  <div className="module-card">
    <div className="module-card-head">
      <div className="mc-info">
        <div className={`mc-icon ${iconClass}`}>{icon}</div>
        <div><h3>{title}</h3>{subtitle && <p>{subtitle}</p>}</div>
      </div>
      {action && <div className="mc-action">{action}</div>}
    </div>
    <div className="module-card-body">{children}</div>
  </div>
);

const Dialog = ({ title, onClose, onConfirm, confirmLabel = 'Confirmar', children, wide = false }) => (
  <div className="dialog-overlay" onClick={onClose}>
    <div className={`dialog-box ${wide ? 'wide' : ''}`} onClick={e => e.stopPropagation()}>
      <header className="dialog-head">
        <h3>{title}</h3>
        <button className="dialog-close" onClick={onClose}><FaTimes /></button>
      </header>
      <div className="dialog-body">{children}</div>
      <footer className="dialog-foot">
        <button className="btn-ghost" onClick={onClose}>Cancelar</button>
        <button className="btn-primary" onClick={onConfirm}>{confirmLabel}</button>
      </footer>
    </div>
  </div>
);

const Field = ({ label, children }) => (
  <div className="form-field"><label>{label}</label>{children}</div>
);

const EmptyState = ({ icon, label, cta }) => (
  <div className="empty-state">
    <div className="es-icon">{icon}</div>
    <p>{label}</p>
    {cta && <button className="btn-primary" onClick={cta.onClick}><FaPlus /> {cta.label}</button>}
  </div>
);

const ModuleLoader = ({ label }) => (
  <div className="module-loader"><FaSpinner className="spin" /><span>{label}</span></div>
);

const FullPageLoader = () => (
  <div className="full-loader"><FaSpinner className="spin fl-spin" /><p>Carregando configurações…</p></div>
);